import React from 'react'
import ReactDOM from 'react-dom/client'
import * as Sentry from '@sentry/react'
import { HelmetProvider } from 'react-helmet-async'
import App from './App'
import './styles/globals.css'
import './styles/marketing-chrome.css'
import { initSentry } from '@/lib/sentry'
import { registerServiceWorker } from '@/lib/registerServiceWorker'

// Init Sentry first so the SDK captures errors during the initial mount.
initSentry('standalone')

// HelmetProvider wraps <App /> at the React root so that <RouteHead>'s
// helmet output (per-route <title>, meta description, OG/Twitter cards,
// JSON-LD) is collected correctly during client-side rendering AND during
// the Path B SSG render in `scripts/prerender.mjs`. Required regardless of
// SSG path — react-helmet-async needs the provider to function safely.
ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    {/* Sentry root error boundary — same safety net as the embed bundle. */}
    <Sentry.ErrorBoundary
      fallback={({ error, resetError }) => (
        <div className="flex min-h-screen items-center justify-center bg-gray-50 p-6 text-center text-gray-900">
          <div className="max-w-md space-y-3">
            <h1 className="text-lg font-semibold">
              Something went wrong.
            </h1>
            <p className="text-sm text-gray-600">
              The error has been reported. Reload to try again.
            </p>
            <pre className="overflow-auto rounded bg-gray-100 p-3 text-left text-xs text-gray-500">
              {error instanceof Error ? error.message : String(error)}
            </pre>
            <button
              type="button"
              onClick={resetError}
              className="rounded bg-blue-600 px-4 py-2 text-sm text-white hover:bg-blue-700"
            >
              Reload
            </button>
          </div>
        </div>
      )}
    >
      <HelmetProvider>
        <App />
      </HelmetProvider>
    </Sentry.ErrorBoundary>
  </React.StrictMode>,
)

// Offline-after-first-load: cache the app shell + static assets via the
// service worker at public/sw.js so the cloud-hosted SPA still boots when
// app.gamedevbuddy.com is unreachable. PROD-only (guarded inside) so it never
// fights Vite's dev HMR; app data stays local via the sidecar regardless.
registerServiceWorker()
